// ===================== Global tunables =====================
export const CONFIG = {
  // World is unbounded ("endless"); these only affect rendering density.
  worldSeed: 1337,
  tileSize: 96,

  // Player defaults (a character def can override any of these).
  player: {
    maxHp: 100,
    moveSpeed: 205,       // px/sec
    pickupRadius: 62,
    damageMult: 1,
    attackSpeedMult: 1,   // (cooldownMult = 1/attackSpeedMult)
    areaMult: 1,
    projectileSpeedMult: 1,
    projMult: 1,          // MULTIPLICATION — multiplies weapon projectile counts
    critChance: 0.05,
    critMult: 2.0,
    armor: 0,
    regen: 0,             // hp/sec
    luck: 0,              // affects rare drops + upgrade rarity
    xpMult: 1,
    goldMult: 1,
    magnetMult: 1,
    invulnAfterHit: 0.5,  // i-frames seconds
    reviveCount: 0,
  },

  // XP curve: xp needed to reach next level. Tuned for the world map's 4-10
  // minute hex runs — a 5-minute Skirmish should land ~24 level-ups (enough to
  // max a weapon and evolve it) and a 10-minute Lair ~37, so every hex delivers
  // a complete build arc instead of ending as the build gets going. Growth is
  // still exponential, so the curve self-limits deep into a long run.
  xp: {
    base: 5,
    growth: 1.15,
    flat: 5,
  },

  // MULTIPLICATION — the golden ✖ pickup's own channel. (The CARD lives in
  // upgrades.js; the two stack and never spend each other.) A pickup's bonus
  // TAPERS: each one is worth `pickupDecay` of the bonus the one before it
  // gave — ×1.30, ×1.27, ×1.24, ×1.22 … Golden ✖ drop on a timer that does not
  // care how long the hex is, so a flat ×1.3 made the pickup channel scale
  // exponentially with run length: nine of them in a 10:00 Lair is ×10.6
  // before a single card, against a five-card ceiling of ×3.05. The taper
  // leaves the early pickups untouched and bends the back half of a long run;
  // every pickup is still a gain, so collecting one is never a mistake.
  multiply: {
    pickupBonus: 0.30,    // the first golden ✖ multiplies projectile count by 1.30
    pickupDecay: 0.90,    // each later one carries 90% of the previous bonus
  },

  spawn: {
    pulse: 0.5,           // seconds between spawn pulses
    rateStart: 2.4,       // enemies/sec at t=0 (sparse early, like the genre)
    ratePerSec: 1 / 42,   // rate grows this much each elapsed second
    rateMax: 26,          // cap on enemies/sec
    spawnRadius: 720,     // distance from player enemies appear
    despawnRadius: 1500,  // far enemies teleport closer
    // On-screen ceilings by device tier (game.start picks via Settings.hordeCap;
    // a run contract may pin its own — the Gauntlet does, for seed parity).
    // Desktop headroom is measured: 400 pinned enemies held 60fps even under
    // software rasterization (2.6ms sim + 4.5ms render at 1000).
    maxEnemies: 160,        // touch devices — the conservative base
    maxEnemiesDesktop: 320, // mouse/keyboard machines take a real horde
  },

  // Difficulty scaling over elapsed run time (seconds). Tuned up — enemies get
  // meaningfully tankier, faster and more dangerous rather than just numerous.
  difficulty: {
    hpPerMin: 0.72,       // +72% enemy hp per minute (linear coeff)
    speedPerMin: 0.08,
    damagePerMin: 0.24,
  },

  colors: {
    neutral: '#c9d2ff',
    fire: '#ff5a2b',
    ice: '#5cd9ff',
    lightning: '#ffe14d',
    poison: '#7dff5c',
    wind: '#b6ffd9',
    holy: '#ffe9a8',
    // Ascendant (tier-2) elements. Weapons tint via CONFIG.colors[element],
    // so every element id — ascended included — needs an entry here, or the
    // ascended forms would render as neutral.
    inferno: '#ff3b14',
    absolutezero: '#9ef2ff',
    ioncascade: '#fff08a',
    blight: '#a4ff3d',
    maelstrom: '#7dffc4',
    zenith: '#fff6d6',
  },
};

// Element ids used throughout.
export const ELEMENTS = ['neutral', 'fire', 'ice', 'lightning', 'poison', 'wind', 'holy'];
